import { lerpColor } from '../../color';
import { hsl, type World as Look } from '../../world';
import { calmAt, calmCenter, glowDot, grain, rgba, spots, starPath, vignette, type Painter, type Spot } from '../../art/paint';

const TAU = Math.PI * 2;

type Seg = readonly [number, number, number, number, number];

interface Tones {
  readonly bg: number;
  readonly far: number;
  readonly ceil: number;
  readonly ink: number;
  readonly rim: number;
  readonly light: number;
  readonly peach: number;
  readonly mint: number;
  readonly coral: number;
  readonly sponge: number;
  readonly brain: number;
  readonly rock: number;
  readonly sand: number;
  readonly weed: number;
}

function tonesOf(look: Look): Tones {
  const pal = look.palette;
  const far = hsl(look.hue, 45, 16);
  return {
    bg: pal.bg,
    far,
    ceil: look.ceil,
    ink: pal.ink,
    rim: pal.rim,
    light: pal.incoming.circle,
    peach: pal.held,
    mint: pal.spot,
    coral: lerpColor(pal.bg, pal.held, 0.36),
    sponge: lerpColor(pal.bg, pal.spot, 0.27),
    brain: lerpColor(look.ceil, lerpColor(pal.bg, pal.held, 0.36), 0.65),
    rock: lerpColor(far, look.ceil, 0.35),
    sand: lerpColor(lerpColor(far, pal.held, 0.14), pal.bg, 0.25),
    weed: lerpColor(far, look.ceil, 0.22),
  };
}

function fixed(i: number): number {
  const v = Math.sin(i * 12.9898 + 78.233) * 43758.5453;
  return v - Math.floor(v);
}

function water(p: Painter, t: Tones): void {
  const { ctx } = p;
  const g = ctx.createLinearGradient(0, 0, 0, p.h);
  g.addColorStop(0, rgba(lerpColor(t.far, t.ceil, 0.35)));
  g.addColorStop(0.45, rgba(t.bg));
  g.addColorStop(1, rgba(lerpColor(t.bg, t.ink, 0.45)));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, p.w, p.h);
  const r = Math.max(p.w, p.h) * 0.75;
  const s = ctx.createRadialGradient(p.w * 0.5, -r * 0.35, r * 0.1, p.w * 0.5, -r * 0.35, r);
  s.addColorStop(0, rgba(t.light, 0.11));
  s.addColorStop(0.5, rgba(t.light, 0.04));
  s.addColorStop(1, rgba(t.light, 0));
  ctx.fillStyle = s;
  ctx.fillRect(0, 0, p.w, p.h);
}

function room(p: Painter, x: number, base: number, want: number, keep: number): number {
  const step = Math.max(4, p.unit * 0.02);
  let h = 0;
  while (h < want && !calmAt(p, x, base - h - step, keep)) h += step;
  return h;
}

function arch(p: Painter, x: number, base: number, rw: number, want: number, color: number): void {
  let rh = want;
  const clear = (): boolean => [0.15, 0.3, 0.5, 0.7, 0.85].every((f) => !calmAt(p, x - rw + 2 * rw * f, base - rh * Math.sin(Math.PI * f), 1.02));
  while (rh > p.unit * 0.15 && !clear()) rh *= 0.92;
  if (!clear()) return;
  const { ctx } = p;
  ctx.beginPath();
  ctx.moveTo(x - rw, base + rh * 0.3);
  const n = 14;
  for (let i = 0; i <= n; i++) {
    const a = Math.PI + (i / n) * Math.PI;
    const k = 1 + p.rng.range(-0.06, 0.06);
    ctx.lineTo(x + Math.cos(a) * rw * k, base + Math.sin(a) * rh * k);
  }
  ctx.lineTo(x + rw, base + rh * 0.3);
  ctx.closePath();
  ctx.moveTo(x + rw * 0.52, base + rh * 0.3);
  ctx.lineTo(x + rw * 0.52, base);
  ctx.ellipse(x, base, rw * 0.52, rh * 0.58, 0, 0, Math.PI, true);
  ctx.lineTo(x - rw * 0.52, base + rh * 0.3);
  ctx.closePath();
  ctx.fillStyle = rgba(color);
  ctx.fill('evenodd');
}

function kelp(p: Painter, x: number, base: number, want: number, frac: number, width: number, color: number): void {
  const height = room(p, x, base, want, 1.08) * frac;
  if (height < width * 4) return;
  const { ctx } = p;
  const phase = p.rng.float() * TAU;
  const amp = width * p.rng.range(0.8, 1.6);
  const n = 18;
  const at = (t: number): number => x + Math.sin(phase + t * 4.2) * amp * t;
  const half = (t: number): number => (width / 2) * (1 - t) ** 0.7 + 0.6;
  ctx.fillStyle = rgba(color);
  ctx.beginPath();
  for (let i = 0; i <= n; i++) ctx.lineTo(at(i / n) - half(i / n), base - (i / n) * height);
  for (let i = n; i >= 0; i--) ctx.lineTo(at(i / n) + half(i / n), base - (i / n) * height);
  ctx.closePath();
  ctx.fill();
  const blades = Math.floor(height / (width * 3));
  for (let j = 1; j < blades; j++) {
    const t = j / blades;
    const side = j % 2 === 0 ? 1 : -1;
    const len = width * 2.2 * (1 - t * 0.6);
    ctx.beginPath();
    ctx.ellipse(at(t) + side * len * 0.5, base - t * height, len * 0.55, width * 0.35 * (1 - t * 0.5), side * -0.5, 0, TAU);
    ctx.fill();
  }
}

function branch(p: Painter, out: Seg[], tips: Spot[], x: number, y: number, len: number, angle: number, width: number, depth: number, key: number, spread: number): void {
  const x1 = x + Math.cos(angle) * len;
  const y1 = y + Math.sin(angle) * len;
  if (calmAt(p, x1, y1, 1.06) || x1 < width || x1 > p.w - width) return;
  out.push([x, y, x1, y1, width]);
  if (depth === 0) {
    tips.push({ x: x1, y: y1 });
    return;
  }
  const j = (fixed(key) - 0.5) * 0.3;
  branch(p, out, tips, x1, y1, len * 0.74, angle - spread + j, width * 0.7, depth - 1, key * 2 + 1, spread);
  branch(p, out, tips, x1, y1, len * 0.74, angle + spread + j, width * 0.7, depth - 1, key * 2 + 2, spread);
}

function strokeSegs(ctx: CanvasRenderingContext2D, segs: readonly Seg[], color: number, extra: number, alpha = 1): void {
  ctx.strokeStyle = rgba(color, alpha);
  ctx.lineCap = 'round';
  for (const s of segs) {
    ctx.lineWidth = s[4] + extra;
    ctx.beginPath();
    ctx.moveTo(s[0], s[1]);
    ctx.lineTo(s[2], s[3]);
    ctx.stroke();
  }
}

function fan(p: Painter, t: Tones, x: number, base: number, r: number, lean: number, ow: number, tips: Spot[]): void {
  const segs: Seg[] = [];
  const ends: Spot[] = [];
  branch(p, segs, ends, x, base, r * 0.62, -Math.PI / 2 + lean * 0.22, r * 0.09, 5, 1, 0.3);
  if (segs.length === 0) return;
  const { ctx } = p;
  const sorted = [...ends].sort((a, b) => Math.atan2(a.y - base, a.x - x) - Math.atan2(b.y - base, b.x - x));
  ctx.beginPath();
  ctx.moveTo(x, base);
  for (const e of sorted) ctx.lineTo(e.x, e.y);
  ctx.closePath();
  ctx.fillStyle = rgba(t.coral, 0.28);
  ctx.fill();
  strokeSegs(ctx, segs, t.ink, ow * 2);
  strokeSegs(ctx, segs, t.coral, 0);
  tips.push(...ends.filter((_, i) => i % 3 === 0));
}

function staghorn(p: Painter, t: Tones, x: number, base: number, r: number, ow: number, tips: Spot[]): void {
  const segs: Seg[] = [];
  const ends: Spot[] = [];
  [-0.8, -0.3, 0.25, 0.75].forEach((a, i) => {
    branch(p, segs, ends, x, base, r * 0.42, -Math.PI / 2 + a, r * 0.12, 2, 40 + i * 7, 0.4);
  });
  const { ctx } = p;
  strokeSegs(ctx, segs, t.ink, ow * 2);
  strokeSegs(ctx, segs, t.coral, 0);
  ctx.fillStyle = rgba(lerpColor(t.coral, t.peach, 0.25));
  for (const e of ends) {
    ctx.beginPath();
    ctx.arc(e.x, e.y, r * 0.045, 0, TAU);
    ctx.fill();
  }
  tips.push(...ends);
}

function sponge(p: Painter, t: Tones, x: number, base: number, h: number, w: number, ow: number, tips: Spot[]): void {
  if (calmAt(p, x, base - h, 1.06) || calmAt(p, x + w * 0.5, base - h * 0.5, 1.06)) return;
  const { ctx } = p;
  ctx.beginPath();
  ctx.moveTo(x - w * 0.42, base);
  ctx.quadraticCurveTo(x - w * 0.62, base - h * 0.5, x - w * 0.5, base - h);
  ctx.lineTo(x + w * 0.5, base - h);
  ctx.quadraticCurveTo(x + w * 0.62, base - h * 0.5, x + w * 0.42, base);
  ctx.closePath();
  ctx.fillStyle = rgba(t.sponge);
  ctx.fill();
  ctx.strokeStyle = rgba(t.ink);
  ctx.lineWidth = ow;
  ctx.stroke();
  ctx.fillStyle = rgba(lerpColor(t.sponge, t.ink, 0.35));
  for (let i = 0; i < 6; i++) {
    ctx.beginPath();
    ctx.arc(x + (fixed(x + i) - 0.5) * w * 0.6, base - h * (0.2 + 0.12 * i), w * 0.06, 0, TAU);
    ctx.fill();
  }
  ctx.beginPath();
  ctx.ellipse(x, base - h, w * 0.5, w * 0.18, 0, 0, TAU);
  ctx.fillStyle = rgba(lerpColor(t.sponge, t.mint, 0.18));
  ctx.fill();
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(x, base - h + w * 0.02, w * 0.34, w * 0.1, 0, 0, TAU);
  ctx.fillStyle = rgba(t.ink);
  ctx.fill();
  tips.push({ x, y: base - h });
}

function brain(p: Painter, t: Tones, x: number, base: number, rx: number, ry: number, ow: number): void {
  const { ctx } = p;
  const shape = new Path2D();
  const n = 22;
  for (let i = 0; i <= n; i++) {
    const a = Math.PI + (i / n) * Math.PI;
    const k = 1 + (fixed(i + 50) - 0.5) * 0.08;
    shape.lineTo(x + Math.cos(a) * rx * k, base + Math.sin(a) * ry * k);
  }
  shape.closePath();
  ctx.fillStyle = rgba(t.brain);
  ctx.fill(shape);
  ctx.save();
  ctx.clip(shape);
  ctx.strokeStyle = rgba(lerpColor(t.brain, t.ink, 0.45));
  ctx.lineWidth = Math.max(1, rx * 0.05);
  ctx.lineCap = 'round';
  for (let j = 0; j < 6; j++) {
    const y0 = base - ry + ((j + 0.6) / 6.2) * ry;
    const f = 2.5 + fixed(j + 70) * 2;
    ctx.beginPath();
    for (let s = 0; s <= 24; s++) {
      const xx = x - rx + (s / 24) * rx * 2;
      ctx.lineTo(xx, y0 + Math.sin((s / 24) * f * TAU + j) * ry * 0.06);
    }
    ctx.stroke();
  }
  const shade = ctx.createRadialGradient(x - rx * 0.3, base - ry * 0.75, ry * 0.1, x, base - ry * 0.3, rx * 1.1);
  shade.addColorStop(0, rgba(t.rim, 0.1));
  shade.addColorStop(0.6, rgba(t.ink, 0));
  shade.addColorStop(1, rgba(t.ink, 0.4));
  ctx.fillStyle = shade;
  ctx.fillRect(x - rx, base - ry * 1.1, rx * 2, ry * 1.1);
  ctx.restore();
  ctx.strokeStyle = rgba(t.ink);
  ctx.lineWidth = ow;
  ctx.stroke(shape);
}

function reef(p: Painter, t: Tones, x: number, base: number, r: number, out: number, fanAt: number, lean: number, ow: number, tips: Spot[]): void {
  fan(p, t, x + fanAt * r, base, r, lean, ow, tips);
  sponge(p, t, x + out * r * 0.78, base, r * 0.9, r * 0.24, ow, tips);
  sponge(p, t, x + out * r * 1.02, base, r * 0.6, r * 0.2, ow, tips);
  staghorn(p, t, x - out * r * 0.3, base, r, ow, tips);
  brain(p, t, x, base, r * 0.62, r * 0.5, ow);
}

function rockPath(ctx: CanvasRenderingContext2D, pts: readonly Spot[], color: number, ink: number, ow: number): void {
  ctx.beginPath();
  for (const q of pts) ctx.lineTo(q.x, q.y);
  ctx.closePath();
  ctx.fillStyle = rgba(color);
  ctx.fill();
  ctx.strokeStyle = rgba(ink);
  ctx.lineWidth = ow;
  ctx.lineJoin = 'round';
  ctx.stroke();
}

function boulders(p: Painter, t: Tones, x: number, top: number, bottom: number, r: number, ow: number): void {
  const { ctx } = p;
  const span = bottom - top;
  const stones: readonly (readonly [number, number, number, number])[] = [
    [x - r * 0.55, bottom - span * 0.18, r * 0.85, span * 0.3],
    [x + r * 0.5, bottom - span * 0.15, r * 0.8, span * 0.26],
    [x + r * 0.08, top + span * 0.5, r * 0.82, span * 0.28],
    [x - r * 0.05, top + span * 0.16, r * 0.72, span * 0.2],
  ];
  stones.forEach(([sx, sy, rx, ry], i) => {
    const shape = new Path2D();
    const n = 16;
    for (let j = 0; j <= n; j++) {
      const a = (j / n) * TAU;
      const k = 1 + (fixed(i * 20 + j) - 0.5) * 0.1;
      shape.lineTo(sx + Math.cos(a) * rx * k, sy + Math.sin(a) * ry * k);
    }
    shape.closePath();
    ctx.fillStyle = rgba(lerpColor(t.rock, t.far, i * 0.12));
    ctx.fill(shape);
    ctx.save();
    ctx.clip(shape);
    const g = ctx.createLinearGradient(sx, sy - ry, sx, sy + ry);
    g.addColorStop(0, rgba(t.rim, 0.07));
    g.addColorStop(0.5, rgba(t.ink, 0));
    g.addColorStop(1, rgba(t.ink, 0.35));
    ctx.fillStyle = g;
    ctx.fillRect(sx - rx * 1.1, sy - ry * 1.1, rx * 2.2, ry * 2.2);
    ctx.restore();
    ctx.strokeStyle = rgba(t.ink);
    ctx.lineWidth = ow;
    ctx.lineJoin = 'round';
    ctx.stroke(shape);
  });
}

function ledge(p: Painter, t: Tones, x0: number, top: number, r: number, ow: number): void {
  const pts: Spot[] = [];
  const n = 10;
  for (let i = 0; i <= n; i++) {
    const x = x0 + ((p.w + 4 - x0) * i) / n;
    pts.push({ x, y: top + (fixed(i + 130) - 0.5) * r * 0.12 });
  }
  for (let i = n; i >= 0; i--) {
    const x = x0 + ((p.w + 4 - x0) * i) / n;
    let y = top + r * 0.25;
    while (y < top + r * 1.8 && !calmAt(p, x, y + 2, 1.02)) y += 2;
    pts.push({ x, y });
  }
  rockPath(p.ctx, pts, t.rock, t.ink, ow);
}

function seabed(p: Painter, t: Tones, top: number): void {
  const { ctx } = p;
  const phase = p.rng.float() * TAU;
  ctx.beginPath();
  ctx.moveTo(-4, p.h + 4);
  for (let x = -4; x <= p.w + 24; x += 20) ctx.lineTo(x, top + Math.sin(x * 0.011 + phase) * p.unit * 0.014 + Math.sin(x * 0.037) * p.unit * 0.005);
  ctx.lineTo(p.w + 24, p.h + 4);
  ctx.closePath();
  const g = ctx.createLinearGradient(0, top, 0, p.h);
  g.addColorStop(0, rgba(t.sand));
  g.addColorStop(1, rgba(lerpColor(t.sand, t.ink, 0.4)));
  ctx.fillStyle = g;
  ctx.fill();
  ctx.strokeStyle = rgba(lerpColor(t.sand, t.ceil, 0.4), 0.25);
  ctx.lineWidth = 1.2;
  ctx.lineCap = 'round';
  for (let i = 0; i < 14; i++) {
    const x = p.rng.float() * p.w;
    const y = top + p.rng.range(0.25, 0.85) * (p.h - top);
    if (calmAt(p, x, y, 1.05)) continue;
    const len = p.unit * p.rng.range(0.04, 0.1);
    ctx.beginPath();
    ctx.moveTo(x - len, y);
    ctx.quadraticCurveTo(x, y - len * 0.18, x + len, y);
    ctx.stroke();
  }
  ctx.fillStyle = rgba(lerpColor(t.sand, t.ink, 0.3));
  for (let i = 0; i < 12; i++) {
    const x = p.rng.float() * p.w;
    const y = top + p.rng.range(0.2, 0.9) * (p.h - top);
    if (calmAt(p, x, y, 1.05)) continue;
    ctx.beginPath();
    ctx.ellipse(x, y, p.unit * p.rng.range(0.008, 0.018), p.unit * p.rng.range(0.005, 0.01), 0, 0, TAU);
    ctx.fill();
  }
}

function scallop(ctx: CanvasRenderingContext2D, x: number, y: number, s: number, rot: number, t: Tones, ow: number): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  ctx.beginPath();
  ctx.moveTo(0, s * 0.35);
  for (let j = 0; j <= 10; j++) {
    const a = -Math.PI / 2 - 0.9 + (1.8 * j) / 10;
    const rr = s * (j % 2 === 0 ? 1 : 0.9);
    ctx.lineTo(Math.cos(a) * rr, s * 0.35 + Math.sin(a) * rr);
  }
  ctx.closePath();
  ctx.fillStyle = rgba(lerpColor(t.sand, t.peach, 0.4));
  ctx.fill();
  ctx.strokeStyle = rgba(t.ink, 0.8);
  ctx.lineWidth = ow * 0.7;
  ctx.stroke();
  ctx.beginPath();
  for (const d of [-0.5, -0.17, 0.17, 0.5]) {
    ctx.moveTo(0, s * 0.35);
    ctx.lineTo(Math.cos(-Math.PI / 2 + d) * s * 0.85, s * 0.35 + Math.sin(-Math.PI / 2 + d) * s * 0.85);
  }
  ctx.stroke();
  ctx.restore();
}

function spiral(ctx: CanvasRenderingContext2D, x: number, y: number, s: number, t: Tones, ow: number): void {
  ctx.beginPath();
  ctx.ellipse(x, y, s, s * 0.75, 0, 0, TAU);
  ctx.fillStyle = rgba(lerpColor(t.sand, t.rim, 0.3));
  ctx.fill();
  ctx.strokeStyle = rgba(t.ink, 0.8);
  ctx.lineWidth = ow * 0.7;
  ctx.stroke();
  ctx.beginPath();
  for (let i = 0; i <= 24; i++) {
    const a = (i / 24) * TAU * 2;
    const rr = s * 0.8 * (1 - i / 28);
    ctx.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr * 0.75);
  }
  ctx.stroke();
}

function shells(p: Painter, t: Tones, top: number, ow: number): void {
  for (let i = 0; i < 6; i++) {
    const x = p.rng.range(0.03, 0.97) * p.w;
    const y = top + p.rng.range(0.3, 0.75) * (p.h - top);
    const s = p.unit * p.rng.range(0.022, 0.034);
    if (calmAt(p, x, y, 1.1)) continue;
    if (i % 2 === 0) scallop(p.ctx, x, y, s, p.rng.range(-0.5, 0.5), t, ow);
    else spiral(p.ctx, x, y, s * 0.8, t, ow);
  }
}

function bubbles(p: Painter, t: Tones, from: readonly Spot[]): void {
  const { ctx } = p;
  for (const f of from) {
    const n = p.rng.int(6, 11);
    const step = p.unit * p.rng.range(0.045, 0.07);
    let y = f.y - step * 0.6;
    for (let i = 0; i < n; i++) {
      const x = f.x + Math.sin(i * 1.3 + f.x) * p.unit * 0.012;
      const r = p.unit * (0.005 + 0.0016 * i) * p.rng.range(0.8, 1.2);
      y -= step * p.rng.range(0.75, 1.25);
      if (y < r * 2) break;
      if (calmAt(p, x, y, 1.05)) continue;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, TAU);
      ctx.strokeStyle = rgba(t.rim, 0.42);
      ctx.lineWidth = Math.max(1, r * 0.3);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(x - r * 0.35, y - r * 0.35, r * 0.28, 0, TAU);
      ctx.fillStyle = rgba(t.rim, 0.45);
      ctx.fill();
    }
  }
}

function jellies(p: Painter, t: Tones): void {
  const { ctx } = p;
  const at = spots(p, 8, 1.12, p.unit * 0.12, p.unit * 0.45).filter((s) => s.y > p.h * 0.22 && s.y < p.h * 0.82).slice(0, 4);
  for (const s of at) {
    const r = p.unit * p.rng.range(0.035, 0.055);
    const a = p.rng.range(0.16, 0.24);
    glowDot(ctx, s.x, s.y, r * 2.6, t.light, a * 0.45);
    ctx.beginPath();
    ctx.arc(s.x, s.y, r, Math.PI, TAU);
    ctx.quadraticCurveTo(s.x, s.y + r * 0.25, s.x - r, s.y);
    ctx.fillStyle = rgba(t.light, a);
    ctx.fill();
    ctx.strokeStyle = rgba(t.light, a * 0.8);
    ctx.lineWidth = Math.max(1, r * 0.08);
    for (const o of [-0.6, -0.2, 0.2, 0.6]) {
      ctx.beginPath();
      ctx.moveTo(s.x + o * r, s.y + r * 0.05);
      ctx.bezierCurveTo(s.x + o * r + r * 0.3, s.y + r * 0.8, s.x + o * r - r * 0.3, s.y + r * 1.4, s.x + o * r, s.y + r * 2.1);
      ctx.stroke();
    }
  }
}

function polyps(p: Painter, t: Tones, tips: readonly Spot[]): void {
  const { ctx } = p;
  const r = Math.max(2, p.unit * 0.008);
  for (const s of tips) {
    glowDot(ctx, s.x, s.y, r * 4, t.mint, 0.3);
    ctx.beginPath();
    ctx.arc(s.x, s.y, r, 0, TAU);
    ctx.fillStyle = rgba(t.mint, 0.55);
    ctx.fill();
  }
  for (let i = 0; i < 46; i++) {
    const x = p.rng.float() * p.w;
    const y = p.rng.float() * p.h;
    if (calmAt(p, x, y, 1.0)) continue;
    ctx.beginPath();
    ctx.arc(x, y, p.rng.range(0.6, 1.4), 0, TAU);
    ctx.fillStyle = rgba(p.rng.chance(0.3) ? t.mint : t.rim, p.rng.range(0.15, 0.35));
    ctx.fill();
  }
}

function school(p: Painter, t: Tones): void {
  const at = spots(p, 6, 1.15, p.unit * 0.2, p.unit * 0.3).find((s) => s.y > p.h * 0.12 && s.y < p.h * 0.6);
  if (at === undefined) return;
  const { ctx } = p;
  const dir = at.x < p.cx ? -1 : 1;
  const s = p.unit * 0.026;
  ctx.fillStyle = rgba(lerpColor(t.far, t.ceil, 0.75));
  for (let i = 0; i < 11; i++) {
    const x = at.x + p.rng.range(-1, 1) * p.unit * 0.16;
    const y = at.y + p.rng.range(-1, 1) * p.unit * 0.08;
    if (calmAt(p, x, y, 1.05)) continue;
    ctx.beginPath();
    ctx.ellipse(x, y, s, s * 0.38, 0, 0, TAU);
    ctx.moveTo(x - dir * s * 0.85, y);
    ctx.lineTo(x - dir * s * 1.5, y - s * 0.4);
    ctx.lineTo(x - dir * s * 1.5, y + s * 0.4);
    ctx.closePath();
    ctx.fill();
  }
}

function starfish(p: Painter, t: Tones, top: number, ow: number): void {
  const { ctx } = p;
  for (let i = 0; i < 4; i++) {
    const x = p.rng.range(0.05, 0.95) * p.w;
    const y = top + p.rng.range(0.35, 0.8) * (p.h - top);
    if (calmAt(p, x, y, 1.1)) continue;
    starPath(ctx, x, y, p.unit * p.rng.range(0.03, 0.042), 0.42, 5, p.rng.float() * TAU);
    ctx.fillStyle = rgba(lerpColor(t.bg, t.peach, 0.45));
    ctx.fill();
    ctx.strokeStyle = rgba(t.ink);
    ctx.lineWidth = ow * 0.8;
    ctx.lineJoin = 'round';
    ctx.stroke();
  }
}

export function paint(p: Painter, look: Look): void {
  const t = tonesOf(look);
  const u = p.unit;
  const ow = Math.max(1.5, u * 0.006);
  const tall = p.w / p.h < 0.8;
  const sandTop = p.h * (tall ? 0.93 : 0.9);
  const tips: Spot[] = [];
  water(p, t);
  if (tall) {
    arch(p, p.w * 0.22, sandTop, u * 0.45, u * 0.5, t.far);
    for (let i = 0; i < 8; i++) {
      const x = p.w * (0.04 + (0.92 * (i + p.rng.range(0.1, 0.9))) / 8);
      kelp(p, x, sandTop + u * 0.03, p.h * 0.34, p.rng.range(0.45, 1), u * p.rng.range(0.028, 0.045), t.weed);
    }
  } else {
    arch(p, p.w * 0.87, sandTop, u * 0.36, u * 0.55, t.far);
    for (let i = 0; i < 12; i++) {
      const left = i % 2 === 0;
      const x = p.w * (left ? p.rng.range(0.02, 0.3) : p.rng.range(0.7, 0.98));
      kelp(p, x, sandTop + u * 0.03, p.h * 0.6, p.rng.range(0.5, 1), u * p.rng.range(0.025, 0.035), t.weed);
    }
  }
  seabed(p, t, sandTop);
  if (tall) {
    const r = u * 0.16;
    const hx = p.w - r * 1.35;
    let base = p.h * 0.3;
    while (base > p.h * 0.2 && calmAt(p, hx - r * 0.62, base - r * 0.1, 1.0)) base -= 2;
    ledge(p, t, hx - r * 1.3, base, r, ow);
    reef(p, t, hx, base, r, 1, -0.15, -0.6, ow, tips);
    reef(p, t, p.w * 0.14, sandTop, r * 0.8, -1, -0.3, -1, ow, tips);
  } else {
    const r = u * 0.26;
    const hx = Math.max(r * 1.25, Math.min(p.w * 0.12, p.cx - u * 1.08 - r * 0.55));
    const base = p.h * 0.64;
    boulders(p, t, hx, base, sandTop + u * 0.04, r, ow);
    reef(p, t, hx, base, r, -1, -0.3, -1, ow, tips);
    const rx = Math.min(p.w - r * 1.2, Math.max(p.w * 0.88, p.cx + u * 1.08 + r * 0.5));
    reef(p, t, rx, sandTop + u * 0.01, r * 0.7, 1, 0.3, 1, ow, tips);
  }
  shells(p, t, sandTop, ow);
  const vents = tips.filter((_, i) => i % 4 === 1).slice(0, 3);
  vents.push({ x: p.w * (tall ? 0.5 : 0.24), y: sandTop }, { x: p.w * (tall ? 0.08 : 0.78), y: sandTop });
  bubbles(p, t, vents);
  if (p.level >= 2) jellies(p, t);
  if (p.level >= 3) polyps(p, t, tips);
  if (p.level >= 4) school(p, t);
  if (p.level >= 5) starfish(p, t, sandTop, ow);
  calmCenter(p, t.bg, 0.6, 1.15);
  grain(p, 0.04, true);
  vignette(p, t.ink, 0.45);
}
